<template>
  <div>
    <!--
    <div class="text-muted">
      {{images.length}} images ({{images.length - Object.keys(imagesIndexed).length}} duplicate)
    </div>
    -->
    <div v-if="selected.length"
      class="py-3 d-flex flex-wrap align-items-center sticky-top frosted eat-gutter" :style="{top: navHeight}"
    >
      <span class="h5 mb-0">
        {{selected.length.toLocaleString()}} selected
      </span>
      <div class="ml-auto"></div>
      <button
        class="btn btn-outline-primary ml-2"
        :disabled="!selected.length"
        v-b-modal.modal-edit
      >Edit multiple</button>
      <button
        class="btn btn-outline-danger ml-2"
        :disabled="!selected.length"
        @click="remove"
      >Remove</button>
    </div>
    <EditBatchFilters
      class="border-top"
      :filterValComputed.sync="filterValComputed"
    >
      <div class="ml-auto"></div>
      <b-spinner
        v-show="busy"
        small
      />
      <div v-if="$slots.info" class="ml-3">
        <slot name="info"/>
      </div>
      <div
        v-if="results.length"
        class="ml-2 px-2"
      >
        <div class="form-control-plaintext">
          <span v-if="filterValComputed">{{activeResults.length.toLocaleString()}} /</span>
          {{results.length | formatCount('row')}}
        </div>
      </div>
      <b-pagination
        v-if="results.length > perPage"
        class="ml-2 mb-0"
        v-model="currentPage"
        :total-rows="activeResults.length"
        :per-page="perPage"
        aria-controls="editTable"
      />
    </EditBatchFilters>

    <b-modal
      ref="modalEdit"
      id="modal-edit"
      title="Edit multiple"
      @ok.prevent="onEdit"
    >
      <OptionsFormMultiple
        ref="formEdit"
        v-model="selected"
        :fields="fieldsEditable"
        :clear-errors="clearErrors"
      ><span/></OptionsFormMultiple>
    </b-modal>

    <b-table
      id="editTable"
      class="edit-table"
      :items="results"
      :fields="fields"
      :filter="filterValComputed"
      :filter-function="filterFn"
      @filtered="onFiltered"
      :busy="busy"
      :empty-text="busy ? 'Loading...' : 'There are no records to show'"
      primary-key="image_name"
      responsive
      show-empty
      :per-page="perPage"
      :current-page="currentPage"
    >
      <template #head(select)>
        <label class="custom-control custom-checkbox custom-control-slim mb-0">
          <input type="checkbox" class="custom-control-input" v-model="selectAll"
            :disabled="!activeResults.length && !selected.length"
          >
          <span class="custom-control-label"></span>
        </label>
      </template>
      <template #cell(select)="row">
        <label class="custom-control custom-checkbox custom-control-slim mb-0">
          <input type="checkbox" class="custom-control-input" v-model="selected" :value="row.item"
            @keydown.enter.prevent="onEnter"
          >
          <span class="custom-control-label"></span>
        </label>
      </template>

      <template #cell(thumb)="row">
        <StatusIndicator
          class="upload-indicator"
          :file="imagesIndexed[row.item.image_name]"
        />
        <div v-if="imagesIndexed[row.item.image_name]" class="text-center">
          <b-img-lazy
            :src="imagesIndexed[row.item.image_name].blob"
            blank-color="#f8f9fa"
            blank-width="64"
            blank-height="64"
            class="thumb-image"
            :class="`orientation-${row.item.orientation}`"
            alt=" "
          />
        </div>
      </template>

      <template #head(location)="{ field }">
        <span :title="field.label" :aria-label="field.label" style="filter: grayscale()">
          📍
        </span>
      </template>
      <template #cell(location)="{ value, index, field }">
        <div v-if="value">
          <a
            :id="`${field.key}-${index}`"
            :href="`https://maps.google.com?q=${value.latitude},${value.longitude}`"
            target="_blank"
            class="btn btn-sm btn-outline-primary border-0 rounded-pill"
          >
            📍
          </a>
          <b-popover
            :target="`${field.key}-${index}`"
            triggers="hover"
            placement="auto"
            boundary="viewport"
          >
            <div style="width:300px" class="mw-100 embed-responsive embed-responsive-1by1">
              <div class="embed-responsive-item d-flex justify-content-center align-items-center">
                Loading map...
              </div>
              <iframe id="gmap_canvas"
                class="embed-responsive-item"
                :src="`https://maps.google.com?q=${value.latitude},${value.longitude}&t=&ie=UTF8&iwloc=&output=embed`"
                frameborder="0" scrolling="no" marginheight="0" marginwidth="0"
              ></iframe>
            </div>
          </b-popover>
        </div>
      </template>

      <template #cell(substrate_sizes)="row">
        <TableToggleButton
          v-if="flatMap(row.value).length || get(row.item.errors, 'substrate_sizes')"
          :active="row.detailsShowing"
          @click="row.toggleDetails"
          class="btn-sm btn-outline-secondary"
        >
          {{flatMap(row.value).length | formatCount('price')}}
        </TableToggleButton>
      </template>

      <template #row-details="row">
        <!-- errors -->
        <div
          v-for="(error, key) in row.item.errors"
          :key="key"
          class="alert"
          :class="`alert-${(row.item._cellVariants && row.item._cellVariants[key]) || row.item._rowVariant}`"
        >
          {{key}}: {{error}}
        </div>

        <!-- substrate_sizes -->
        <div
          v-if="row.item.substrate_sizes"
          class="d-flex flex-wrap justify-content-center"
        >
          <div
            v-for="(sizes, substrate) in row.item.substrate_sizes"
            :key="substrate"
            class="m-2"
          >
            <div class="p-2 bg-secondary text-light text-capitalize">
              {{substrate}}
            </div>
            <b-table
              table-class="text-right size-table"
              small borderless
              :items="highlightSizeErrors(sizes)"
            >
              <template #cell(pricecents)="size_row">
                {{currency(size_row.value, 100)}}
              </template>
            </b-table>
          </div>
        </div>
      </template>

      <template v-for="field in keysEditable" #[`cell(${field})`]="row">
        <input
          type="text"
          class="form-control cell-input"
          @keydown.enter.prevent="onEnter"
          v-model="row.item[field]"
          @change="clearError(row.item, field)"
          :key="field"
        >
      </template>

      <template #cell(substrates)="{ item }">
        <input
          type="text"
          class="form-control cell-input"
          v-model="item.substrates"
          @change="clearError(item, 'substrates')"
          @keydown.enter.prevent="onEnter"
          placeholder="all"
        />
      </template>

      <template #cell()="row">
        <div class="pl-input">{{row.value}}</div>
      </template>
    </b-table>

    <div
      class="d-flex align-items-center mb-3"
      v-if="results.length > perPage"
    >
      <div class="ml-auto"></div>
      <div class="ml-2 px-2">
        <span v-if="filterValComputed">{{activeResults.length.toLocaleString()}} /</span>
        {{results.length | formatCount('row')}}
      </div>
      <b-pagination
        class="ml-2 mb-0"
        v-model="currentPage"
        :total-rows="activeResults.length"
        :per-page="perPage"
        aria-controls="editTable"
      />
    </div>
  </div>
</template>

<script>
import { flatMap, get, mapValues } from 'lodash'

import EditBatchFilters, { filterFn } from '@/components/EditBatchFilters.vue'
import OptionsFormMultiple from '@/components/OptionsFormMultiple.vue'
import TableToggleButton from '@/components/TableToggleButton.vue'
import StatusIndicator from '@/components/StatusIndicator.vue'
import { extension } from '@/utils/extension.js'
import { omit } from '@/utils/omit.js'
import { currency } from '@/utils/currency.js'

export default {
  name: 'EditBatch',
  components: {
    EditBatchFilters,
    OptionsFormMultiple,
    TableToggleButton,
    StatusIndicator,
  },
  props: {
    data: Array,
    metadata: Object,
    images: Array,
    busy: Boolean,
  },
  data() {
    return {
      selected: [],
      currentPage: 1,
      perPage: 100,
      filterValComputed: null,
      activeResults: [],
    }
  },
  computed: {
    navHeight() {
      return this.$store.state.appearance.navHeight
    },
    results() {
      const data = this.data
      if (!data) { return [] }
      return data.map(row => {
        // remove warnings in real time
        if (row._cellVariants) {
          const filledCells = Object.keys(row._cellVariants).filter(key => {
            return key === 'thumb' && this.imagesIndexed[row.image_name]
          })
          row._cellVariants = omit(row._cellVariants, filledCells)
          row.errors = omit(row.errors, filledCells)
        }
        return row
      })
    },
    fields() {
      const result = [
        {key: 'select', class: 'checkable-column'},
        {key: 'thumb', tdClass: 'position-relative'},
        ...Object.keys(this.metadata).map(key => ({key})),
      ]
      result.forEach(field => {
        // add title attributes to cells
        field.tdAttr = (value, key, item) => ({title: getTitle(item, key)})
      })
      return result
    },
    fieldsEditable() {
      return omit(this.metadata, [
        'image_name',
        'substrate_sizes',
        'location',
      ])
    },
    keysEditable() {
      return Object.keys(this.fieldsEditable)
    },
    imagesIndexed() {
      if (!this.images) { return {} }
      return this.images.reduce((acc, cur) => {
        const key = extension(cur.file.name, false)
        acc[key] = cur
        return acc
      }, {})
    },
    selectAll: {
      get: function () {
        // selectAll should remain true when filtering shows a subset of the selected results
        const { selected, activeResults } = this
        return selected.length
          && selected.length >= activeResults.length
             // fast isSubsetOf from https://stackoverflow.com/a/55644245/490592
          && new Set([...activeResults, ...selected]).size === selected.length
      },
      set: function (value) {
        this.selected = value ? this.activeResults : []
      },
    },
  },
  watch: {
    results: {
      handler(val) {
        this.activeResults = val
      },
      immediate: true,
    },
    selected: {
      handler(val) {
        this.$emit('update:hasSelection', Boolean(val.length))
      },
      immediate: true,
    },
  },
  methods: {
    filterFn,
    remove() {
      this.$emit('update:hasSelection', false)
      this.$emit('remove', this.selected)
      this.selected = []
    },
    async onEdit() {
      await this.$refs.formEdit.onSubmit()
      this.$nextTick(() => {
        this.$refs.modalEdit.hide()
      })
    },
    onEnter(event) {
      this.cellNav(event.target, event.shiftKey ? 'up' : 'down')
    },
    cellNav(el, direction) {
      const cell = el.closest('td')
      const row = cell.parentElement
      const index = Array.prototype.indexOf.call(row.children, cell);
      const newRow = direction === 'up' ? previousRow(row) : nextRow(row)
      const newEl = newRow?.children[index].querySelector('input, textarea, button, select')
      if (!newEl) { return }
      newEl.focus()
      newEl.select?.()
    },
    currency,
    flatMap,
    get,
    clearError(item, field) {
      if (get(item, ['_cellVariants', field])) {
        item._cellVariants = omit(item._cellVariants, [field])
      }
      if (get(item, ['errors', field])) {
        item.errors = omit(item.errors, [field])
      }
    },
    clearErrors(item, fields) {
      if (item._cellVariants) {
        item._cellVariants = omit(item._cellVariants, fields)
      }
      if (item.errors) {
        item.errors = omit(item.errors, fields)
      }
    },
    highlightSizeErrors(sizes) {
      return sizes.map(size => ({
        ...size,
        _cellVariants: mapValues(size, (i) => {
          if (!i) { return 'danger' } // missing value
        }),
      }))
    },
    onFiltered(val) {
      this.activeResults = val
      this.currentPage = 1
    },
  },
}

function getTitle(item, field) {
  const val = item[field]
  const err = item.errors?.[field]
  return formatTitle(err, '\n\n', prefixWarning) || formatTitle(val)
}
function formatTitle(val, separator = ', ', iteratee = i=>i) {
  if (val?.toString?.() === '[object Object]') {
    return // Ignore plain objects, as they often do not work well as titles.
  }
  return val?.map?.(iteratee).join?.(separator) // array
    || iteratee(val) // string, etc
}
function prefixWarning(val) {
  return val && `⚠️ ${val}`
}

function nextRow(tr) {
  return tr.classList.contains('b-table-has-details')
    ? tr.nextElementSibling.nextElementSibling
    : tr.nextElementSibling
}
function previousRow(tr) {
  const prev = tr.previousElementSibling
  return prev?.classList.contains('b-table-details')
    ? prev.previousElementSibling
    : prev
}
</script>

<style lang="scss" scoped>
.edit-table {
  /deep/ {
    th {
      white-space: nowrap;
    }
    > table > tbody > tr > td {
      padding: 3px;
      vertical-align: middle;
    }
  }
}

.cell-input {
  background: transparent;
  border-color: transparent;
  border-radius: 0;
  &:focus {
    min-width: 300px;
  }
}

.pl-input {
  padding-left: $input-padding-x;
}

.size-table {
  border-collapse: separate;
  border-spacing: 0 1px;
}

.upload-indicator {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%,-50%);
  font-size: 2em;
  text-shadow: 0 2px 0 black;
}
/deep/ .checkable-column {
  width: 2.75rem;
  text-align: center;
}

.thumb-image {
  max-width: 100%;
  max-height: 64px;
  object-fit: contain; // workaround for https://crbug.com/1243158
  image-orientation: from-image;
}

// orientation handling
@supports not (image-orientation: from-image) {
  .contain {
    width: 64px;
    height: 64px;
    object-fit: contain;
  }
  .orientation-3 { transform: rotate(180deg); }
  .orientation-6 { @extend .contain; transform: rotate(90deg); }
  .orientation-8 { @extend .contain; transform: rotate(-90deg); }
}
</style>
